import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, NumberInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Recycle, Plus, Trash2 } from "lucide-react";
import { fmtINR, fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import TID from "@/constants/testIds";
import { ImportExportBar } from "@/components/foundation/ImportExportBar";

export default function ScrapRates() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        code: "", name: "", rate_per_kg: 0, is_active: true,
        effective_date: new Date().toISOString().slice(0, 10), status: "ACTIVE",
    });
    const [submitting, setSubmitting] = useState(false);
    const canEdit = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        api.get("/scrap-rates").then((r) => setRows(r.data)).catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({
            code: "", name: "", rate_per_kg: 0, is_active: true,
            effective_date: new Date().toISOString().slice(0, 10), status: "ACTIVE",
        });
        setOpen(true);
    };
    const openEdit = (r) => {
        setEditing(r);
        setForm({
            code: r.code, name: r.name, rate_per_kg: r.rate_per_kg, is_active: r.is_active,
            effective_date: r.effective_date ? r.effective_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
            status: r.status || (r.is_active ? "ACTIVE" : "INACTIVE"),
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e?.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...form, rate_per_kg: parseFloat(form.rate_per_kg) };
            if (editing) await api.put(`/scrap-rates/${editing.id}`, payload);
            else await api.post("/scrap-rates", payload);
            toast.success(editing ? "Scrap rate updated" : "Scrap rate added");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete scrap rate ${row.code}? This action cannot be undone.`)) return;
        try {
            await api.delete(`/scrap-rates/${row.id}`);
            toast.success("Scrap rate deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid={TID.scrapPage}>
            <PageHeader
                eyebrow="FOUNDATION · LAYER 3"
                title="Scrap Rates"
                subtitle="Material recovery credit applied during should-cost calculation. Updated quarterly per scrap dealer rates."
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ImportExportBar entity="scrap-rates" label="Scrap Rates" canImport={canEdit} onImported={load} />
                        {canEdit && (
                            <CopperButton onClick={openCreate} data-testid={TID.scrapAddBtn}>
                                <Plus className="h-4 w-4" /> Add Scrap Type
                            </CopperButton>
                        )}
                    </div>
                }
            />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}
            {rows && rows.length === 0 && <EmptyState icon={Recycle} title="No scrap rates yet" />}

            {rows && rows.length > 0 && (
                <Card>
                    <div className="overflow-x-auto">
                    <table data-testid={TID.scrapTable} className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-4">Code</th>
                                <th className="eyebrow text-left py-2 px-4">Name</th>
                                <th className="eyebrow text-right py-2 px-4">Rate (₹/kg)</th>
                                <th className="eyebrow text-left py-2 px-4">Updated</th>
                                <th className="eyebrow text-left py-2 px-4">Status</th>
                                <th className="eyebrow text-right py-2 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-slate-100">
                                    <td className="py-1.5 px-4 text-[13px] font-mono font-semibold">{r.code}</td>
                                    <td className="py-1.5 px-4 text-[13px]">{r.name}</td>
                                    <td className="py-1.5 px-4 text-[13px] font-mono tabular-nums text-right font-semibold">{fmtINR(r.rate_per_kg)}</td>
                                    <td className="py-1.5 px-4 text-[12px] text-slate-500">{fmtRelative(r.updated_at)}</td>
                                    <td className="py-1.5 px-4"><Pill tone={r.is_active ? "emerald" : "slate"}>{r.is_active ? "Active" : "Inactive"}</Pill></td>
                                    <td className="py-1.5 px-4 text-right">
                                        {canEdit && (
                                            <div className="inline-flex gap-1">
                                                <SlateButton onClick={() => openEdit(r)} className="h-7 px-2.5 text-xs">Edit</SlateButton>
                                                <SlateButton
                                                    onClick={() => remove(r)}
                                                    variant="ghost"
                                                    className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                                    title={`Delete ${r.code}`}
                                                    data-testid={`scrap-delete-${r.id}`}
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </SlateButton>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </Card>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Scrap Rate" : "Add Scrap Rate"}
                footer={<>
                    <SlateButton variant="outline" onClick={() => setOpen(false)}>Cancel</SlateButton>
                    <CopperButton onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save"}</CopperButton>
                </>}
            >
                <form onSubmit={submit} className="space-y-5">
                    <Field label="Scrap Code" required>
                        <TextInput value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="MS-MEDIUM" required disabled={!!editing} />
                    </Field>
                    <Field label="Description" required>
                        <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="MS Medium Melting Scrap" required />
                    </Field>
                    <Field label="Rate (₹/kg)" required>
                        <NumberInput value={form.rate_per_kg} onChange={(e) => setForm({ ...form, rate_per_kg: e.target.value })} step="0.01" required />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Effective Date">
                            <TextInput type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="scrap-effective-date" />
                        </Field>
                        <Field label="Status">
                            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="scrap-status">
                                <option value="ACTIVE">Active</option>
                                <option value="INACTIVE">Inactive</option>
                            </Select>
                        </Field>
                    </div>
                </form>
            </Modal>
        </div>
    );
}